import React, { useLayoutEffect } from 'react';
import Portal from 'components/Portal';
import { H6 } from 'components/Typography';
import PropTypes from 'prop-types';
import {
  ModalContainer,
  Backdrop,
  ModalHeader,
  ModalCloseButton,
  ModalBody
} from './styled';

const Modal = ({
  isOpen,
  thickness,
  background,
  color,
  header,
  onClose,
  children,
  isScrollable,
  headingColor,
  variant,
  className,
  headerLogo,
  ...props
}) => {
  useLayoutEffect(() => {
    if (isOpen) {
      document.body.style.overflowY = 'hidden';
      document.documentElement.style.height = '100%';
    }
    return () => {
      document.body.style.overflowY = null;
      document.documentElement.style.height = null;
    };
  }, [isOpen]);

  const backdropVariants = {
    hidden: {
      opacity: 0,
      pointerEvents: 'none'
    },
    visible: {
      opacity: 1,
      pointerEvents: 'all'
    }
  };

  const modalVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };

  return (
    <Portal name="modal-portal">
      <Backdrop
        onMouseDown={(e) => {
          e.stopPropagation();
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
        isScrollable={isScrollable}
        initial="hidden"
        animate={isOpen ? 'visible' : 'hidden'}
        variants={backdropVariants}
        transition={{ duration: 0.2 }}
        variant={variant}
      >
        <ModalContainer
          onClick={(e) => e.stopPropagation()}
          className={className}
          color={color}
          background={background}
          variant={variant}
          variants={modalVariants}
          initial="hidden"
          transition={{ stiffness: 100 }}
        >
          {Boolean(headerLogo) && headerLogo}
          {header && (
            <ModalHeader
              thickness={thickness}
              headingColor={headingColor}
              variant={variant}
            >
              <H6>{header}</H6>
            </ModalHeader>
          )}
          <ModalCloseButton onClick={onClose} variant={variant} />

          <ModalBody
            hasHeader={!!header}
            thickness={thickness}
            {...props}
            variant={variant}
          >
            {children}
          </ModalBody>
        </ModalContainer>
      </Backdrop>
    </Portal>
  );
};

Modal.propTypes = {
  onClose: PropTypes.func.isRequired,
  background: PropTypes.oneOf(['primary', 'secondary']),
  thickness: PropTypes.oneOf(['normal', 'thick']),
  variant: PropTypes.oneOf(['normal', 'thick', 'rh', 'rhFluid', 'fullScreen']),
  color: PropTypes.oneOf(['light', 'dark']),
  children: PropTypes.any.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isScrollable: PropTypes.bool,
  header: PropTypes.string,
  headingColor: PropTypes.string,
  className: PropTypes.string,
  headerLogo: PropTypes.node
};

Modal.defaultProps = {
  header: null,
  thickness: 'normal',
  background: 'primary'
};

export default Modal;
